import styled from '@emotion/styled';
import * as React from 'react';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import { getDefaultThumbnail, getLink, getLinkPath } from '@models/Course';
import useSubscribed from '@hooks/useSubscribed';
import segmentStorage from '@events/segmentStorage';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { mediaQuery, Spacings } from '@styles/index';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMeUid } from '@api/selectors/auth';
import { wordsToSentence, getCommaSeparatedUIDs } from '@utils/textHelper';
import { evtNameCategoryMap, programmeTypes } from '@constants/index';
import { SEARCH_RESULT_CLICKED, SEARCH_RESULT_PAGE } from '@constants/segment';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import { updateRecentSearchLog } from '@api/fetchers/search/search';
import CourseCard from 'aquilla/core/organisms/CourseCard';
import pushRoute from '@utils/pushRoute';
import CoursePlaceholder from './placeholderCourseThumbail/CoursePlaceholder';
import SvgThumbail from './placeholderCourseThumbail/SvgThumbnail';
import { getAuthorInfo, getCourseInfo } from './util';

interface IOrientation {
  $orientation: 'horizontal' | 'vertical';
}

const SvgCourseThumbnail = styled(SvgThumbail)<IOrientation>`
  width: 272px;
  height: ${({ $orientation }) =>
    $orientation === 'horizontal' ? '152px' : 'auto'};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    border-radius: 0;
  }
`;

const CourseThumbnail = styled(Thumbnail)`
  width: 272px;
  height: ${({ orientation }) => orientation === 'horizontal' && '152px'};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    border-radius: 0;
  }
`;

const PlaceholderThumbnail = styled(CoursePlaceholder)`
  height: 100%;
  width: 100%;
  transform: scale(1.02);
  background-color: var(--color-divider);
  object-fit: cover;
  @media only screen and (max-width: 47.95em) {
    transform: scale(1);
  }
`;

const DefaultThumbnail = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${Spacings.SPACING_2B};
`;

const StyledCourseCard = styled(CourseCard)`
  min-width: 272px;
`;

interface IPlusCourseCard {
  orientation?: IOrientation['$orientation'];
  course: any;
  topic?: any;
  type?: string;
  imageBorderRadius?: string;
  showProgress?: boolean;
  dataAnalytics?: string;
  index: number;
  label?: string;
  lastPrimarySource?: string;
  searchData?: any;
  hideLessonCount?: boolean;
  onCardClick?: () => void;
}

const getTopicName = (topicGroups, topic) => {
  let topicName = topicGroups?.[0]?.name || topicGroups?.[0]?.title || '';

  if (topic && topic.uid) {
    topicName = topic.name;
  }
  return topicName;
};

const PlusCourseCard: React.FC<IPlusCourseCard> = ({
  orientation = 'vertical',
  topic = {},
  type = '',
  imageBorderRadius = '0px',
  showProgress = false,
  dataAnalytics = '',
  label = '',
  lastPrimarySource = '',
  searchData = {},
  hideLessonCount = false,
  onCardClick = () => {},
  course,
  index
}) => {
  const { authors, language, showAsLesson, nextTrack, name } = course;
  const displayName = showAsLesson ? nextTrack.name : name;
  const goalUID = course?.goalUid || course?.goal?.uid;
  const isSubscribed = useSubscribed(goalUID);
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const goalSegmentData = useGoalEventProps({ goalUID });
  const { topicGroups } = course;
  const topicName = getTopicName(topicGroups, topic);
  const { color } = getTopicGroupAssetObj(topicName);
  const meUID = useMyInfo(getMeUid);

  const coverPhoto =
    course.coverPhotoV1 ||
    course.coverPhoto ||
    course.thumbnail ||
    getDefaultThumbnail(course);

  const showProgressbar =
    isLoggedIn && isSubscribed && course.itemCount > 0 && showProgress;

  if (!course) return null;

  const { isLive } = course;

  const handleAnalytics = () => {
    const courseData = course || {};
    const courseUID = courseData.uid || '';
    // Means card was rendered on a search page
    if (course.showAsLesson) {
      segment.track('Lesson Viewed', {
        last_primary_source: lastPrimarySource,
        last_primary_source_section: label
      });
    }
    if (type === 'search') {
      const nSearchData = { ...searchData };
      delete nSearchData.query;
      const position = nSearchData.position ? nSearchData.position : 'See all';
      // send segment search result clicked event
      segment.track(
        SEARCH_RESULT_CLICKED,
        {
          ...goalSegmentData,
          sub_position: index + 1,
          is_object_plus: courseData.forPlus,
          is_object_special: courseData.isSpecial,
          educator_uid: getCommaSeparatedUIDs(authors),
          educator_username: getCommaSeparatedUIDs(authors, 'username'),
          educator_name: getCommaSeparatedUIDs(authors, 'name'),
          last_primary_source: SEARCH_RESULT_PAGE,
          last_primary_source_section: 'Courses',
          search_section: 'Global',
          current_screen: 'Courses',
          eventName: SEARCH_RESULT_CLICKED,
          eventType: 'click',
          version: 2,
          objectID: `${searchData.object_type}:${courseUID}`,
          userToken: meUID,
          ...nSearchData,
          position
        },
        evtNameCategoryMap.WITHOUT_CATEGORY
      );
      updateRecentSearchLog({
        ...searchData,
        entity_id: `${searchData.object_type}:${courseUID}`,
        goal_uid: goalUID
      });
    }
    if (lastPrimarySource === 'lesson page') {
      segment.track(course.isSpecial ? 'CLX Lesson View' : 'Course Viewed', {
        last_primary_source: lastPrimarySource,
        last_primary_source_section: label || segmentStorage.getItem('lpss'),
        course_uid: course.uid,
        course_title: course.name,
        start_date: course.startsAt,
        end_date: course.endsAt,
        educator_uid: wordsToSentence(course.authors, 'uid', ',', ','),
        educator_username: wordsToSentence(
          course.authors,
          'username',
          ',',
          ','
        ),
        is_offline_course:
          courseData?.courseType === programmeTypes.HYBRID_COURSE,
        ...goalSegmentData
      });
    }
    onCardClick();

    // Adding LPSS to segment from label property and deleting it on class exit
    // in case property is not deleted removing it if label is not passed
    if (label) segmentStorage.setItem('lpss', label);
    else segmentStorage.deleteItem('lpss');

    const route = {
      href: getLinkPath(course),
      as: getLink(course)
    };
    pushRoute(route.href, route.as);
  };

  const getVariant = () => {
    if (isLive) return 'live';
    return showProgressbar ? 'continue_watching' : 'missed';
  };

  const thumbail = () => {
    return coverPhoto ? (
      <CourseThumbnail
        width={272}
        height={152}
        layout="intrinsic"
        alt={course.newThumbnail ? 'new-thumbnail' : 'thumbnail'}
        imgUrl={coverPhoto}
        orientation={orientation}
        borderRadius={imageBorderRadius}
        data-analytics={`${dataAnalytics}-courseCard`}
      />
    ) : (
      <SvgCourseThumbnail
        // @ts-ignore
        as={DefaultThumbnail}
        $orientation={orientation}
      >
        <PlaceholderThumbnail color={color} />
      </SvgCourseThumbnail>
    );
  };
  return (
    <StyledCourseCard
      key={course.uid}
      detailsLine={getCourseInfo(hideLessonCount, course)}
      educatorName={getAuthorInfo(course).authorName}
      href={getLink(course)}
      langLabel={language}
      onClick={handleAnalytics}
      progress={(course.lessonsCompleted / course.itemCount) * 100}
      thumbnail={thumbail}
      title={displayName}
      topicColor={color}
      topicName={topicName}
      educatorLink={getAuthorInfo(course).authorID}
      variant={getVariant()}
    />
  );
};

export default PlusCourseCard;
